import { GOOGLE_API, GOOGLE_MAP_ID, GOOGLE_STATIC } from 'app-config/index.js'
const earthRadius = 6371008.7714 // metres
const pi = Math.PI
const advancedMarkerWarnings = new Set()

export const withGoogleMapId = options => {
    const merged = options && Object.prototype.hasOwnProperty.call(options, 'mapId') ? { ...options } : { ...(options || {}), mapId: GOOGLE_MAP_ID }
    if (merged.mapId == null || merged.mapId === '') return merged
    // google-map-react always merges default POI label styles; with mapId, styles must come from Cloud Console only.
    return { ...merged, styles: null }
}

export const areAdvancedMarkersAvailable = ({ map, maps, context = 'Google Maps' } = {}) => {
    if (!map || !maps) return false
    const hasMarkerLibrary = Boolean(maps?.marker?.AdvancedMarkerElement)
    const mapCapabilities = map.getMapCapabilities?.()
    const isAvailable = hasMarkerLibrary && (mapCapabilities ? mapCapabilities.isAdvancedMarkersAvailable !== false : true)

    if (!isAvailable && context && !advancedMarkerWarnings.has(context)) {
        advancedMarkerWarnings.add(context)
        const hint = hasMarkerLibrary
            ? 'Map ID config likely failed (check API key + Map ID in the same GCP project, referrer restrictions, billing, or blocked requests to maps.googleapis.com e.g. ad blockers).'
            : 'Load the "marker" library and initialize the map with a valid mapId.'
        console.warn(`${context}: Advanced markers unavailable. ${hint}`)
    }

    return isAvailable
}

export const googleMapStaticImage = props => {
    const zoom = props?.zoom || 20
    const size = props?.size || '90x90'
    if (props?.shape) {
        let polygon = []
        /* eslint array-callback-return: 0 */
        if (props?.shape?.path)
            Object.keys(props?.shape?.path).map(loc => {
                polygon.push(`${props?.shape.path[loc].latitude},${props?.shape.path[loc].longitude}`)
            })
        if (props?.shape.type === 'polygon') polygon.push(`${props?.shape.path[0].latitude},${props?.shape.path[0].longitude}`)
        let fillColor = props?.shape.color && props?.shape.opacity ? String(props?.shape.color).replace('#', '') + decToHex(props?.shape.opacity) : '00ff00ff'
        let pathColor = props?.shape.color ? String(props?.shape.color).replace('#', '') + 'DD' : '00ff00ff'
        return `${GOOGLE_STATIC}${props?.lat ? 'center=' + props?.lat + ',' + props?.lng + '&' : ''}size=${size}&maptype=satellite&path=color:0x${pathColor}|fillcolor:0x${props?.shape.type === 'polygon' ? fillColor : ''}|weight:4|${polygon.join(
            '|',
        )}&key=${GOOGLE_API}`
    } else {
        let icon = ''
        if (props?.icon) {
            icon = `&markers=icon:${props?.icon}|${props?.lat},${props?.lng}`
        }
        return `${GOOGLE_STATIC}center=${props?.lat},${props?.lng}&zoom=${zoom}&size=${size}&maptype=satellite${icon}&key=${GOOGLE_API}`
    }
}
const decToHex = dec => {
    return (Math.round(dec * 255) + 0x10000).toString(16).substr(-2).toUpperCase()
}
export const averageGeolocation = coords => {
    if (!coords) return coords
    if (!coords || (coords && coords.length === 1)) {
        return coords[0] || coords
    }
    let x = 0.0
    let y = 0.0
    let z = 0.0
    for (let coord of coords) {
        let latitude = (coord.latitude * pi) / 180
        let longitude = (coord.longitude * pi) / 180
        x += Math.cos(latitude) * Math.cos(longitude)
        y += Math.cos(latitude) * Math.sin(longitude)
        z += Math.sin(latitude)
    }
    const total = coords.length
    x = x / total
    y = y / total
    z = z / total
    const centralLongitude = Math.atan2(y, x)
    const centralSquareRoot = Math.sqrt(x * x + y * y)
    const centralLatitude = Math.atan2(z, centralSquareRoot)
    return {
        latitude: (centralLatitude * 180) / pi,
        longitude: (centralLongitude * 180) / pi,
    }
}
export async function reverseGeocode(maps, lat, lng) {
    if (!maps || !lat || !lng) return null
}

export const pathDistance = coords => {
    if (!coords || coords.length < 2) return 0
    let distance = 0
    for (let i = 0; i < coords.length - 1; i++) {
        const φ1 = coords[i].latitude.toRadians()
        const φ2 = coords[i + 1].latitude.toRadians()
        const Δφ = (coords[i + 1].latitude - coords[i].latitude).toRadians()
        const Δλ = (coords[i + 1].longitude - coords[i].longitude).toRadians()
        const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
        distance += earthRadius * c
    }
    return distance
}
export const latLngOffset = (lat, lng, latM, lngM) => {
    const m = 1 / (((2 * pi) / 360) * earthRadius) / 1000
    return {
        lat: lat + latM * m,
        lng: lng + (lngM * m) / Math.cos(lng * (pi / 180)),
    }
}

export const computeOffset = (from, distance, heading, radius = earthRadius) => {
    const fromLat = from.latitude.toRadians()
    const fromLng = from.longitude.toRadians()
    const radHeading = heading.toRadians()
    const cosDistance = Math.cos(distance / radius)
    const sinDistance = Math.sin(distance / radius)
    const sinFromLat = Math.sin(fromLat)
    const cosFromLat = Math.cos(fromLat)
    const sc = cosDistance * sinFromLat + sinDistance * cosFromLat * Math.cos(radHeading)
    const newLat = Math.asin(sc).toDegrees()
    const newLng = (fromLng + Math.atan2(sinDistance * cosFromLat * Math.sin(radHeading), cosDistance - sinFromLat * sc)).toDegrees()
    return { latitude: newLat, longitude: newLng }
}

/* eslint no-extend-native: 0 */
Number.prototype.toRadians = function () {
    return (this * pi) / 180
}
/* eslint no-extend-native: 0 */
Number.prototype.toDegrees = function () {
    return (this * 180) / pi
}
